// 先代の姫の弱攻撃「数えながら突く」の作り直し3案（2026-11-04）。
//   作者（11-04 jab=fix）「手をずっと上に上げながら切ってのはダサすぎるな。別の方法を考えなきゃかもしれない。
//   そもそも根本的に変える方法も考えなきゃいけないかもしれない。」
//   前の模型（princess_count_jab.ts）は奥の手を顔の横に上げたまま突いていた。3案とも、突く間の奥の手は腰に当てる（立ち絵イの「左手を腰」）。
//   A 引き手で折る＝突きを引く瞬間だけ奥の手を顔の前へ出して1本折り、腰へ戻す。
//   B 構えで数える＝突きの間は腰のまま。突き終えた構えで手を上げ、1本ずつ折って見せてから合図の技。
//   C 光で数える＝指は使わない。突くたびに剣先から青い光（声・一粒の灯）が1つ離れて頭の上に並び、合図の技で剣先へ集まる。
//   拍は3案とも前の模型と同じ仮の値（主人公の弱攻撃を読ませる分だけ伸ばした物）。B だけ数える構えの分だけ合図の技が遅れる。

import { humanPose } from '../body'
import { bakeTracks, trackAt, type MockupMove, type MockupPhase, type TrackKey, type Tracks, type Vec } from '../core'

interface Beat { j1: number; j2: number; tell: number; lunge: number }
const BEAT_AC: Beat = { j1: 660, j2: 1260, tell: 1600, lunge: 1900 }
const BEAT_B: Beat = { j1: 660, j2: 1260, tell: 2200, lunge: 2500 }
/** 合図の技の当たりから一周の終わりまで */
const AFTER = 1100

const STAND = { px: 0, py: -70, lean: 6, tw: 0, ht: 0, aF: 40, bF: 10, sF: 4, aB: 120, bB: 68, sB: 0, fF: [12, 0] as Vec, fB: [-10, 0] as Vec }
type RigKey = keyof typeof STAND
/** 奥の手の形：腰に当てる／顔の前で指を見せる／前へ突き出して見せる／踏み込みで後ろへ流す */
const ARM = { hip: [120, 68], face: [10, -60], show: [-10, -30], back: [150, 140] } as const
type ArmKey = keyof typeof ARM

const build = (v: Record<string, number | Vec>) => {
  const r = { ...STAND, ...v }
  const extra: Record<string, number | Vec> = {}
  for (const k in v) if (!(k in STAND)) extra[k] = v[k]!
  return { ...humanPose(r as unknown as Parameters<typeof humanPose>[0]), ...extra }
}

/** 足を1歩運ぶ：真上へ上げてから運び、真上から下ろす（princess_count_jab.ts と同じ）。上げが低いと膝がほとんど曲がらず、伸びた脚が浮いて見えるので 16 以上にする。 */
const step = (t0: number, t1: number, x0: number, x1: number, h: number): TrackKey[] => {
  const d = t1 - t0, dx = x1 - x0
  return [[t0, [x0, 0], 'io'], [t0 + d * 0.3, [x0 + dx * 0.15, -h], 'io'], [t1 - d * 0.3, [x1 - dx * 0.15, -h * 0.7], 'io'], [t1, [x1, 0], 'io']]
}

/** 1回分の突き（振りかぶり→突き→戻り）。奥の手は含めない。stepBack＝踏み出した前足を戻すか（合図がすぐ続く時は踏み出したまま）。 */
const jab = (at: number, stepBack = true): Partial<Record<RigKey, TrackKey[]>> => ({
  px: [[at - 240, 0, 'io'], [at - 100, -2, 'io'], [at, 10, 'in'], [at + 260, stepBack ? 0 : 8, 'io']],
  // 突く時は腰を落とす（腰が前へ出た分、膝を曲げないと足が床に届かず、伸びきった脚が浮いて見えるため）
  py: [[at - 240, -70, 'io'], [at, -65, 'in'], [at + 260, stepBack ? -68 : -66, 'io']],
  lean: [[at - 100, 0, 'io'], [at, 14, 'in'], [at + 260, 6, 'io']],
  tw: [[at - 100, -18, 'io'], [at, 22, 'in'], [at + 260, 0, 'io']],
  aF: [[at - 100, 120, 'io'], [at, 2, 'in'], [at + 260, 40, 'io']],
  bF: [[at - 100, 10, 'io'], [at, -4, 'in'], [at + 260, 10, 'io']],
  sF: [[at - 100, 0, 'io'], [at, 2, 'in'], [at + 260, 4, 'io']],
  fF: [...step(at - 160, at, 12, 28, 16), ...(stepBack ? step(at + 200, at + 380, 28, 12, 16) : [])],
})

/** 2回の突き→合図→大きく踏み込む突き→戻り（奥の手以外の体）。 */
function bodyTracks(b: Beat): Tracks {
  // 2回目の突きから合図まで短い時は、前足を戻さず踏み出した所から大きく踏み込む（戻す足と踏み込む足が続けて浮き、倒れて見えるため）
  const keep = b.tell - b.j2 < 500, f0 = keep ? 28 : 12
  const j1 = jab(b.j1), j2 = jab(b.j2, !keep), T = b.tell, L = b.lunge
  const cat = (k: RigKey, extra: TrackKey[] = []): TrackKey[] => [[0, STAND[k] as number | Vec], ...(j1[k] ?? []), ...(j2[k] ?? []), ...extra]
  return {
    px: cat('px', [[T, 10, 'io'], [L - 80, 4, 'io'], [L, 40, 'in'], [L + 400, 40], [L + 800, 10, 'io'], [L + 1050, 0, 'io']]),
    py: cat('py', [[T, -68, 'io'], [L - 80, -60, 'io'], [L, -54, 'in'], [L + 400, -54], [L + 800, -70, 'io']]),
    lean: cat('lean', [[T, 2, 'io'], [L - 80, -4, 'io'], [L, 26, 'in'], [L + 400, 26], [L + 800, 6, 'io']]),
    tw: cat('tw', [[T, -10, 'io'], [L - 80, -30, 'io'], [L, 30, 'in'], [L + 400, 30], [L + 800, 0, 'io']]),
    aF: cat('aF', [[T, 70, 'io'], [L - 80, 150, 'io'], [L, -6, 'in'], [L + 400, -6], [L + 800, 40, 'io']]),
    bF: cat('bF', [[T, 20, 'io'], [L - 80, 0, 'io'], [L, -10, 'in'], [L + 400, -10], [L + 800, 10, 'io']]),
    sF: cat('sF', [[T, 6, 'io'], [L - 80, -4, 'io'], [L, -2, 'in'], [L + 400, -2], [L + 800, 4, 'io']]),
    // 戻りは片足ずつ（両足が同時に浮かない）
    fF: cat('fF', [...step(L - 120, L, f0, 64, 22), ...step(L + 520, L + 720, 64, 24, 10), ...step(L + 860, L + 950, 24, 12, 8)]),
    fB: [[0, [-10, 0]], ...step(L + 400, L + 520, -10, 4, 9), ...step(L + 950, L + 1060, 4, -10, 7)],
  }
}

/** 奥の手の形の並び → aB・bB の時間の線 */
function armTracks(keys: [number, ArmKey][]): Tracks {
  return {
    aB: keys.map(([t, k], i) => [t, ARM[k][0], i ? 'io' : undefined] as TrackKey),
    bB: keys.map(([t, k], i) => [t, ARM[k][1], i ? 'io' : undefined] as TrackKey),
  }
}
/** 合図（前へ突き出して見せる）→踏み込みで後ろへ流す→腰へ戻す */
const tellArm = (b: Beat): [number, ArmKey][] =>
  [[b.tell + 120, 'show'], [b.lunge - 80, 'show'], [b.lunge + 80, 'back'], [b.lunge + 400, 'back'], [b.lunge + 800, 'hip']]

function mockup(id: string, label: string, b: Beat, tr: Tracks, phases: MockupPhase[], cues: number[], source: string): MockupMove {
  const dur = b.lunge + AFTER
  return {
    id, boss: '先代の姫', label, body: 'human-rapier-skirt', durMs: dur, scale: 0.95, baseX: 300, heroAt: 150, interp: 'linear',
    keys: bakeTracks(tr, dur, build), phases, hits: [b.j1, b.j2, b.lunge], activeMs: 60, box: [40, 120, -128, -88], paths: ['sF'],
    cues: cues.map(at => ({ at, ms: 80 })), source,
  }
}
const jabPhases = (b: Beat, j1: string, j2: string): MockupPhase[] => [
  { from: 0, to: 400, label: '構え：左手は腰', kind: 'rest' },
  { from: 400, to: 1000, label: j1, kind: 'hit' },
  { from: 1000, to: b.j2 + 340, label: j2, kind: 'hit' },
]
const lungePhases = (b: Beat, tell: string): MockupPhase[] => [
  { from: b.tell, to: b.lunge, label: tell, kind: 'tell' },
  { from: b.lunge, to: b.lunge + 400, label: '合図の技：大きく踏み込んで突く', kind: 'hit' },
  { from: b.lunge + 400, to: b.lunge + AFTER, label: '戻り', kind: 'rest' },
]
const SRC = '拍は前の版と同じ仮の値。帯の黄色＝数が1つ進んだ瞬間。数値は技を組む時と数値を詰める時に決め直す。'

// ---------- A 引き手で折る ----------
const A = BEAT_AC
export const PRINCESS_TELL_PULL_BEAT = { ...A, fold1: [A.j1 + 180, A.j1 + 260], fold2: [A.j2 + 180, A.j2 + 260] }
const PA = PRINCESS_TELL_PULL_BEAT
export const PRINCESS_TELL_PULL_MOCKUP = mockup('princess-tell-pull', '指折り A 引き手で折る（突きを引く瞬間だけ左手を顔の前へ出して1本折る）', A, {
  ...bodyTracks(A),
  ...armTracks([[0, 'hip'], [A.j1 + 60, 'hip'], [A.j1 + 200, 'face'], [A.j1 + 340, 'face'], [A.j1 + 520, 'hip'],
    [A.j2 + 60, 'hip'], [A.j2 + 200, 'face'], [A.tell, 'face'], ...tellArm(A)]),
  nUp: [[0, 5], [PA.fold1[0], 5], [PA.fold1[1], 4, 'io'], [PA.fold2[0], 4], [PA.fold2[1], 3, 'io'], [A.lunge + 500, 3], [A.lunge + 800, 5, 'io']],
}, [...jabPhases(A, '1回目の突き・引きながら1本折る', '2回目の突き・引きながら1本折る'),
  ...lungePhases(A, '合図：3本を前へ突き出して見せ、腰を落とす')], [PA.fold1[1], PA.fold2[1]], SRC)

// ---------- B 構えで数える ----------
const Bb = BEAT_B
export const PRINCESS_TELL_STANCE_BEAT = { ...Bb, raise: [Bb.j2 + 300, Bb.j2 + 500], fold1: [1840, 1920], fold2: [2040, 2120] }
const PB = PRINCESS_TELL_STANCE_BEAT
export const PRINCESS_TELL_STANCE_MOCKUP = mockup('princess-tell-stance', '指折り B 構えで数える（突きの間は腰のまま・突き終えた構えで1本ずつ折る）', Bb, {
  ...bodyTracks(Bb),
  ...armTracks([[0, 'hip'], [PB.raise[0], 'hip'], [PB.raise[1], 'face'], [Bb.tell, 'face'], ...tellArm(Bb)]),
  nUp: [[0, 5], [PB.fold1[0], 5], [PB.fold1[1], 4, 'io'], [PB.fold2[0], 4], [PB.fold2[1], 3, 'io'], [Bb.lunge + 500, 3], [Bb.lunge + 800, 5, 'io']],
}, [...jabPhases(Bb, '1回目の突き（左手は腰のまま）', '2回目の突き（左手は腰のまま）'),
  { from: Bb.j2 + 340, to: Bb.tell, label: '構え：左手を上げ、1本ずつ折る', kind: 'tell' },
  ...lungePhases(Bb, '合図：3本を前へ突き出して見せ、腰を落とす')], [PB.fold1[1], PB.fold2[1]], SRC)

// ---------- C 光で数える ----------
const Cb = BEAT_AC
/** 光が並ぶ所（頭の上・体の原点から） */
const SLOTS: Vec[] = [[-4, -168], [-36, -158]]
function tipAt(tr: Tracks, t: number): Vec {
  const v: Record<string, number | Vec> = {}
  for (const k in tr) v[k] = trackAt(tr[k]!, t)
  const p = build(v), h = p.hF as Vec, a = (p.sF as number) * Math.PI / 180
  return [h[0] + Math.cos(a) * 64, h[1] + Math.sin(a) * 64]
}
const lightTracks = (): Tracks => {
  const body = { ...bodyTracks(Cb), ...armTracks([[0, 'hip'], [Cb.lunge - 200, 'hip'], [Cb.lunge + 80, 'back'], [Cb.lunge + 400, 'back'], [Cb.lunge + 800, 'hip']]) }
  const out: Tracks = { ...body }
  const end = tipAt(body, Cb.lunge)
  ;[Cb.j1, Cb.j2].forEach((at, i) => {
    const born = tipAt(body, at)
    // 剣先で生まれ→頭の上へ流れて並ぶ→合図で少し前へ寄る→踏み込みの当たりで剣先へ集まり、消える
    out['l' + i] = [[0, born], [at, born], [at + 320, SLOTS[i]!, 'out'], [Cb.tell, SLOTS[i]!], [Cb.lunge - 120, [SLOTS[i]![0] + 14, SLOTS[i]![1] + 6], 'io'], [Cb.lunge, end, 'io']]
    out['la' + i] = [[0, 0], [at - 1, 0], [at, 1], [Cb.lunge, 1], [Cb.lunge + 160, 0, 'out']]
  })
  return out
}
export const PRINCESS_TELL_LIGHT_BEAT = { ...Cb, born: [Cb.j1, Cb.j2] }
export const PRINCESS_TELL_LIGHT_MOCKUP = mockup('princess-tell-light', '指折り C 光で数える（指は使わず、突くたびに青い光が1つ頭の上に並ぶ）', Cb, lightTracks(),
  [...jabPhases(Cb, '1回目の突き・剣先から光が1つ離れる', '2回目の突き・光がもう1つ'),
    ...lungePhases(Cb, '合図：並んだ光が前へ寄り、腰を落とす')], [Cb.j1, Cb.j2],
  '拍は前の版と同じ仮の値。帯の黄色＝光が生まれた瞬間。光は声の姿「一粒の灯」の仮の形。数値は技を組む時と数値を詰める時に決め直す。')

/** 部屋の灯りの3案（princess_stage_lights.ts）が同じ体の動きを使う */
export { BEAT_AC, STAND, step, bodyTracks, armTracks, build, mockup, jabPhases, lungePhases }

export const PRINCESS_TELL_MOCKUPS = [PRINCESS_TELL_PULL_MOCKUP, PRINCESS_TELL_STANCE_MOCKUP, PRINCESS_TELL_LIGHT_MOCKUP]
